import { Context, createContext } from 'react';
import {
    CognitoUser,
    CognitoUserPool,
    CognitoUserSession,
    CognitoIdToken,
    CognitoAccessToken,
    CognitoRefreshToken,
} from 'amazon-cognito-identity-js';
import getPool from '../UserPool';
import { useNavigate } from 'react-router-dom';
import { useParams } from "react-router-dom";

import { useState } from "react";
import { StorageService } from '../services/StorageService';
import { LoginAuthService, LoginTokens, LoginAuthResponse, LoginChallenge } from '../services/LoginAuthService';
import { DEFAULT_CLIENT_ID, DEFAULT_USER_POOL_ID } from '../shared/Config';
import { generateRedirectUri, navigateToPage, navigateToRedirectUri } from '../services/navigateService';
import { PageEnum } from '../enums/pageEnum';
import { IndexedService } from '../services/IndexedService';

const AccountContext: Context<any> = createContext(null);

interface ChallengeState {
    challenge_session: string
    email: string
    client_id: string
    pool_id: string
    kind: LoginChallenge["challenge"]
}

const Account = (props: any) => {
    const [routeParams, setRouteParams] = useState(useParams())
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const urlQuery = new URLSearchParams(queryString);
    const params = Object.fromEntries(urlParams.entries());

    let Pool: CognitoUserPool | null = null
    if (params.client_id) {
        Pool = getPool(routeParams.pool_id || "", params.client_id)
    } else {
        Pool = getPool(DEFAULT_USER_POOL_ID, DEFAULT_CLIENT_ID) // default redirect without params
    }

    const [, setResult] = useState<any>(null);
    const [cognitoUser, setCognitoUser] = useState<CognitoUser | null>(null);
    const [challenge, setChallenge] = useState<ChallengeState | null>(null);
    const [userInfo, setUserInfo] = useState<any>(null);

    const navigate = useNavigate();

    function hydrateCognitoUserFromTokens(poolId: string, clientId: string, tokens: LoginTokens): CognitoUser {
        const localPool = getPool(poolId, clientId)
        const user = new CognitoUser({ Username: tokens.email, Pool: localPool })
        const session = new CognitoUserSession({
            IdToken: new CognitoIdToken({ IdToken: tokens.id_token }),
            AccessToken: new CognitoAccessToken({ AccessToken: tokens.access_token }),
            RefreshToken: new CognitoRefreshToken({ RefreshToken: tokens.refresh_token }),
        })
        user.setSignInUserSession(session)
        return user
    }

    async function saveTokensAndRedirect(tokens: any, paramsFromComponent: any, email?: any) {
        const queryString = window.location.search;
        const urlParams = new URLSearchParams(queryString);
        const params = Object.fromEntries(urlParams.entries());

        tokens.authorization_code = params.authorization_code // get authorization_code from URL params

        // Send to API storage
        if (!params.authorization_code) {
            return navigateToRedirectUri(params, tokens)
        }

        try {
            const response = await StorageService.getStorage(tokens, paramsFromComponent)
            await IndexedService.setTransactionDB(paramsFromComponent.pool_id, response.data.session, email)

            if(params && params.redirect_uri) {
                const urlString = new URL(params.redirect_uri)

                if (urlString.protocol !== 'http:' && urlString.protocol !== 'https:') {
                    let redirect_uri = generateRedirectUri(params)
                    return navigateToPage(navigate, urlQuery, PageEnum.LOGGED_IN, {redirect_uri}, false, urlParams)
                }
            }

            return navigateToRedirectUri(params)
        } catch (e) {
            return navigateToRedirectUri({
                error: "time_expired",
                error_description: "Time expired, please try again",
                redirect_uri: params.redirect_uri,
            })
        }
    }

    function getCognitoUser() {
        return cognitoUser
    }

    function getChallengeSession() {
        return challenge
    }

    async function getSession() {
        return await new Promise((resolve, reject) => {
            if (cognitoUser) {
                cognitoUser.getSession((err: any, session: any) => {
                    if (err) {
                        reject();
                    } else {
                        resolve(session);
                    }
                });
            } else {
                reject();
            }
        });
    }

    async function finishLoginWithTokens(
        tokens: LoginTokens,
        poolId: string,
        clientId: string,
        routeParamsFromComponent: any,
    ) {
        const user = hydrateCognitoUserFromTokens(poolId, clientId, tokens)
        setCognitoUser(user)
        setChallenge(null)

        await saveTokensAndRedirect(tokens, routeParamsFromComponent, tokens.email)
    }

    function applyChallenge(
        response: Extract<LoginAuthResponse, { kind: "challenge" }>,
        email: string,
        clientId: string,
        poolId: string,
        routeParamsFromComponent: any,
    ) {
        const next: ChallengeState = {
            challenge_session: response.challenge_session,
            email,
            client_id: clientId,
            pool_id: poolId,
            kind: response.challenge,
        }
        setChallenge(next)
        setResult("")

        if (response.challenge === "TOTP") {
            return navigateToPage(navigate, routeParamsFromComponent, PageEnum.TOPT, null, true)
        }
        if (response.challenge === "NEW_PASSWORD") {
            setResult({ isFirstLogin: true })
            return navigateToPage(navigate, routeParamsFromComponent, PageEnum.UPDATE_PASSWORD, null, true)
        }
    }

    async function authenticate(
        Username: string,
        Password: string,
        poolId: string,
        clientId: string,
        routeParamsFromComponent: any,
    ) {
        setResult("")

        const response = await LoginAuthService.login(poolId, routeParamsFromComponent.tenant, clientId, Username, Password)

        if (response.kind === "tokens") {
            await finishLoginWithTokens(response, poolId, clientId, routeParamsFromComponent)
            return
        }

        applyChallenge(response, Username, clientId, poolId, routeParamsFromComponent)
    }

    async function totpAuth(totpCode: string, routeParamsFromComponent: any) {
        if (!challenge) {
            throw Object.assign(new Error("LOGIN_CHALLENGE_EXPIRED"), { status: 0, messageCode: "LOGIN_CHALLENGE_EXPIRED" })
        }

        const response = await LoginAuthService.respondTotp(
            challenge.pool_id,
            routeParamsFromComponent.tenant,
            challenge.client_id,
            challenge.challenge_session,
            totpCode,
        )

        if (response.kind === "tokens") {
            await finishLoginWithTokens(response, challenge.pool_id, challenge.client_id, routeParamsFromComponent)
            return
        }

        applyChallenge(response, challenge.email, challenge.client_id, challenge.pool_id, routeParamsFromComponent)
    }

    async function updatePassword(newPassword: string, routeParamsFromComponent?: any) {
        if (!challenge) {
            throw Object.assign(new Error("LOGIN_CHALLENGE_EXPIRED"), { status: 0, messageCode: "LOGIN_CHALLENGE_EXPIRED" })
        }

        const effectiveRouteParams = routeParamsFromComponent || routeParams

        const response = await LoginAuthService.respondNewPassword(
            challenge.pool_id,
            effectiveRouteParams.tenant,
            challenge.client_id,
            challenge.challenge_session,
            newPassword,
        )

        if (response.kind === "tokens") {
            await finishLoginWithTokens(response, challenge.pool_id, challenge.client_id, effectiveRouteParams)
            return
        }

        applyChallenge(response, challenge.email, challenge.client_id, challenge.pool_id, effectiveRouteParams)
    }

    function logout() {
        if (!Pool) return

        const user = Pool.getCurrentUser();
        if (user) {
            user.signOut();
            navigate(0)
        }
    }

    function setInfoUser(data: any) {
        setUserInfo(data)
    }

    function getInfoUser() {
        return userInfo
    }

    return (
        <AccountContext.Provider value={{
            authenticate,
            getSession,
            logout,
            updatePassword,
            totpAuth,
            getCognitoUser,
            getChallengeSession,
            setRouteParams,
            setInfoUser,
            getInfoUser
        }}>
            {props.children}
        </AccountContext.Provider>
    );
};

export { Account, AccountContext };